import { useState } from "react";
import { authClient } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle("Sign in");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await authClient.signIn.email({ email: email.trim(), password });
      if ("error" in res && res.error) {
        throw new Error(res.error.message ?? "Sign in failed");
      }
      toast.success("Signed in");
      // App redirects to ?next= once the session refetch makes `me` available;
      // navigating here would race it and lose the deep link.
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sign in failed";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative grid h-screen w-screen place-items-center overflow-hidden bg-background p-6">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute -left-32 top-1/4 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="absolute -right-32 bottom-1/4 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
      </div>
      <Card className="relative z-10 w-full max-w-sm border-border/40 bg-card/80 p-7 backdrop-blur">
        <div className="mb-6 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-foreground text-xs font-bold tracking-tighter text-background">
            BP
          </span>
          <div>
            <div className="text-sm font-semibold leading-none">BillyPocket</div>
            <div className="mt-0.5 flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
              <ShieldCheck className="h-2.5 w-2.5" /> admin console
            </div>
          </div>
        </div>
        <h1 className="text-lg font-semibold">Sign in</h1>
        <p className="mt-1 text-xs text-muted-foreground">Restricted access for BillyPocket admins.</p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
              {error}
            </div>
          )}
          <Button type="submit" className="mt-1 w-full" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
